import { createPassiveLeavingHintTracker, RecordingWireClient, type PassiveLeavingHintTracker } from "@uploads";

const DRAIN_SETTLE_MS = 15_000;
const LOG_PREFIX = "[LSP-21294] admin-upload leaving-hint";

export interface AdminUploadBatchToken {
	readonly conversationId: string;
	ended: boolean;
}

interface UploadEpisode {
	inFlight: number;
	fullyDrained: boolean;
	timer?: ReturnType<typeof setTimeout>;
}

const episodes = new Map<string, UploadEpisode>();
let tracker: PassiveLeavingHintTracker | undefined;
let listenersInstalled = false;

function apiRoot(): string {
	return (window.env?.VITE_API_ROOT ?? "").replace(/\/$/, "");
}

function getTracker(): PassiveLeavingHintTracker {
	if (!tracker) {
		tracker = createPassiveLeavingHintTracker({
			wire: new RecordingWireClient({ baseUrl: apiRoot() }),
			laneLabel: "admin-upload"
		});
	}
	return tracker;
}

function clearEpisodeTimer(conversationId: string, episode: UploadEpisode): void {
	if (episode.timer === undefined) return;
	clearTimeout(episode.timer);
	episode.timer = undefined;
	console.debug(`${LOG_PREFIX} timer superseded by a new batch, ConversationId=${conversationId}`);
}

export function noteAdminUploadCompleted(conversationId: string | undefined): void {
	try {
		const leavingHintTracker = getTracker();
		leavingHintTracker.noteContentCompleted(conversationId);
		if (listenersInstalled) return;
		try {
			leavingHintTracker.install();
			listenersInstalled = true;
		} catch (error) {
			console.warn(
				`${LOG_PREFIX} listener installation failed; will retry on next completion, ConversationId=${conversationId ?? "(none)"}`,
				error
			);
		}
	} catch (error) {
		console.warn(`${LOG_PREFIX} completion note threw, ConversationId=${conversationId ?? "(none)"}`, error);
	}
}

export function beginAdminUploadBatch(conversationId: string): AdminUploadBatchToken {
	const token: AdminUploadBatchToken = { conversationId, ended: false };
	try {
		let episode = episodes.get(conversationId);
		if (!episode) {
			episode = { inFlight: 0, fullyDrained: true };
			episodes.set(conversationId, episode);
		}
		clearEpisodeTimer(conversationId, episode);
		episode.inFlight += 1;
		console.debug(`${LOG_PREFIX} batch began, InFlight=${episode.inFlight}, ConversationId=${conversationId}`);
	} catch (error) {
		console.warn(`${LOG_PREFIX} batch begin threw, ConversationId=${conversationId}`, error);
	}
	return token;
}

export function endAdminUploadBatch(token: AdminUploadBatchToken | undefined, fullyDrained: boolean): void {
	const conversationId = token?.conversationId;
	try {
		const episode = conversationId ? episodes.get(conversationId) : undefined;
		if (!token || token.ended || !episode || episode.inFlight <= 0) {
			console.warn(`${LOG_PREFIX} unbalanced end ignored, FullyDrained=${fullyDrained}, ConversationId=${conversationId ?? "(none)"}`);
			return;
		}

		token.ended = true;
		episode.inFlight -= 1;
		episode.fullyDrained &&= fullyDrained;
		console.debug(
			`${LOG_PREFIX} batch ended, FullyDrained=${fullyDrained}, InFlight=${episode.inFlight}, ConversationId=${conversationId}`
		);
		if (episode.inFlight > 0) return;
		if (!episode.fullyDrained) {
			episodes.delete(conversationId);
			console.debug(`${LOG_PREFIX} dirty episode completed without a timer, ConversationId=${conversationId}`);
			return;
		}

		episode.timer = setTimeout(() => {
			try {
				episode.timer = undefined;
				console.debug(`${LOG_PREFIX} timer fired, InFlight=${episode.inFlight}, ConversationId=${conversationId}`);
				if (episode.inFlight > 0) {
					console.debug(`${LOG_PREFIX} timer skipped because an episode is in flight, ConversationId=${conversationId}`);
					return;
				}
				// LSP-22802: a batch finishing while the agent is still on the dashboard is not a departure. Sending
				// the hint here put the conversation on the server's short leaving wait (sticky for 10 minutes), so
				// a pause of a couple of minutes before the next batch released it early. Leaving the page still
				// sends the tracker's own visibilitychange/pagehide hint.
				if (document.visibilityState !== "hidden") {
					console.info(`${LOG_PREFIX} drained hint skipped — dashboard is still visible, ConversationId=${conversationId}`);
					episodes.delete(conversationId);
					return;
				}
				const hintSent = getTracker().sendCompletionHint(conversationId);
				if (!hintSent) {
					console.warn(`${LOG_PREFIX} drained hint could not be sent, ConversationId=${conversationId}`);
				}
				episodes.delete(conversationId);
			} catch (error) {
				console.warn(`${LOG_PREFIX} timer handler threw, ConversationId=${conversationId}`, error);
			}
		}, DRAIN_SETTLE_MS);
		console.debug(`${LOG_PREFIX} timer armed, SettleMs=${DRAIN_SETTLE_MS}, ConversationId=${conversationId}`);
	} catch (error) {
		console.warn(`${LOG_PREFIX} batch end threw, FullyDrained=${fullyDrained}, ConversationId=${conversationId ?? "(none)"}`, error);
	}
}

export function resetAdminLeavingHintForTests(): void {
	try {
		for (const episode of episodes.values()) {
			if (episode.timer !== undefined) clearTimeout(episode.timer);
		}
		episodes.clear();
		tracker?.destroy();
		tracker = undefined;
		listenersInstalled = false;
	} catch (error) {
		console.warn(`${LOG_PREFIX} test reset threw, ConversationId=(none)`, error);
	}
}
